import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  MarkerClusterer,
  Circle,
} from "@react-google-maps/api";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardHeader from "../../components/header/DashboardHeader";
import Pagination from "../../components/pagination/Pagination";
import {
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import config from "../../config.js";
import "./stops.css";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import { useTranslation } from "react-i18next";
import SubTitle from "../../components/common/SubTitle";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";
import Skeleton from "@mui/material/Skeleton";
import {
  getAllRoutes,
  getAllStops,
  getAllVehicles,
  getVehicleDirection,
} from "../../components/dashboard/dashboardAPI.js";
import Footer from "../../components/common/Footer.jsx";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets.js";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#647490",
    color: "white",
    fontSize: 20,
    fontWeight: 600,
    width: "20%",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 24,
  },
}));

const colors = [
  "#20A8E0",
  "#8FBC8F",
  "#FFD700",
  "#6495ED",
  "#FF69B4",
  "#FFA07A",
  "#ADFF2F",
  "#FF4500",
  "#9370DB",
  "#FFFFFF",
];

const libraries = ["places", "directions", "geometry"];

const LoadingScreen = ({ t, navigate, selectedStopData, localStopsData }) => {
  const handleViewStop = () => {
    navigate(`/track/screen/${selectedStopData.id}`, {
      state: { selectedStopData, localStopsData },
    });
  };

  const handleStopSchedules = () => {
    navigate("/stop/Schedule/Details", {
      state: { id: selectedStopData.id },
    });
  };

  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title={t("Monitor Stop")}
        path={t("Stops > Monitor Stop")}
        track="Stop View"
        add="View Schedules"
        onTrackClick={handleViewStop}
        onAddClick={handleStopSchedules}
      />

      <Box
        sx={{
          padding: { xs: "0.9375rem", sm: "1.25rem", md: "1.875rem" },
          margin: { xs: "0 0.9375rem", sm: "0 1.25rem", md: "0 2.5rem" },
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
        }}
      >
        {/* Top Info Section Skeleton */}
        <Box
          sx={{
            borderRadius: "0.625rem",
            border: "1px solid #A5DCF3",
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 1px 5px 1px #00000040",
            padding: "1.25rem",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: "1.25rem",
          }}
        >
          {[...Array(4)].map((_, index) => (
            <React.Fragment key={index}>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  height="1.5rem"
                  sx={{ mx: "auto", mb: "0.5rem" }}
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  height="2rem"
                  sx={{ mx: "auto" }}
                />
              </Box>
              {index < 3 && (
                <Skeleton
                  variant="rectangular"
                  width={{ xs: "100%", sm: "1px" }}
                  height={{ xs: "1px", sm: "3.75rem" }}
                  sx={{ alignSelf: "center" }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Map Section Skeleton */}
        <Box
          sx={{
            borderRadius: "0.625rem",
            border: "1px solid #A5DCF3",
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 1px 5px 1px #00000040",
            padding: "1.25rem",
            margin: {
              xs: "0.625rem 0.9375rem",
              sm: "0.625rem 1.25rem",
              md: "1.25rem 2.5rem",
            },
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="34.375rem"
            sx={{ borderRadius: "1.25rem" }}
          />
        </Box>

        {/* Table Section Skeleton */}
        <Box
          sx={{
            borderRadius: "0.625rem", // Converted 10px to rem
            border: "1px solid #A5DCF3",
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 1px 5px 1px #00000040",
            padding: "1.25rem", // Converted 20px to rem
            margin: {
              xs: "0.625rem 0.9375rem",
              sm: "0.625rem 1.25rem",
              md: "0.625rem 2.5rem",
            }, // Converted 10px, 15px, 20px, 40px to rem
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="3rem"
            sx={{ mb: "0.5rem" }}
          />{" "}
          {/* Table header */}
          {[...Array(3)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height="2rem"
              sx={{ mb: "0.5rem" }}
            /> // Table rows
          ))}
        </Box>
      </Box>
    </>
  );
};

const AdminStopView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedStopData, localStopsData } = location.state || {};
  const [stopData, setStopData] = useState(null);
  const [allStopData, setAllStopData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitialVehicleLoadComplete, setIsInitialVehicleLoadComplete] =
    useState(false);
  const [directions, setDirections] = useState([]);
  const [vehicleData, setVehicleData] = useState({});
  const [error, setError] = useState(null);
  const routePathsRef = useRef({});
  const [map, setMap] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);
  const [vehicleHistory, setVehicleHistory] = useState({});
  const [stoppedVehicles, setStoppedVehicles] = useState({});
  const vehicleHistoryRef = useRef({});
  const stoppedVehiclesRef = useRef({});
  const stopDataRef = useRef(null);
  const handleSSEMessageRef = useRef(null);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [userState, setUserState] = useState(null);
  const [hasMapPermission, setHasMapPermission] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [distanceWithStop, setDistanceWithStop] = useState([]);
  const [vehiclesAtStop, setVehiclesAtStop] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [hoveredVehicle, setHoveredVehicle] = useState(null);
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const { settings } = useSettings();
  const { getIntermediateStopIcon, getVehicleIcon: getCustomVehicleIcon } =
    useMapIcons(settings, filteredVehicles, filteredStops, filteredRoutes);

  // Helper functions
  const normalizeDeviceData = useCallback((data) => {
    const latitude = data.latitude ?? data.Latitude;
    const longitude = data.longitude ?? data.Longitude;
    const deviceId = data.deviceId ?? data.device_id;

    if (deviceId && latitude != null && longitude != null) {
      return { ...data, deviceId, latitude, longitude };
    }

    return {
      ...data,
      deviceId,
      latitude,
      longitude,
      timestamp: new Date().toISOString(),
      rawData: data.RawData || "",
    };
  }, []);

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const calculateHeading = useCallback((prev, current) => {
    if (!prev || !current) return null;
    return window.google.maps.geometry.spherical.computeHeading(
      new window.google.maps.LatLng(prev.lat, prev.lng),
      new window.google.maps.LatLng(current.lat, current.lng),
    );
  }, []);

  const snapToRoute = useCallback((routeId, position) => {
    const routePath = routePathsRef.current[routeId];
    if (!routePath) return position;
    const point = new window.google.maps.LatLng(position.lat, position.lng);
    let closestPoint = point;
    let minDistance = Infinity;
    for (let i = 0; i < routePath.length - 1; i++) {
      const start = routePath[i];
      const end = routePath[i + 1];
      const projectedPoint = window.google.maps.geometry.spherical.interpolate(
        start,
        end,
        0.5,
      );
      const distance =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          point,
          projectedPoint,
        );
      if (distance < minDistance) {
        closestPoint = projectedPoint;
        minDistance = distance;
      }
    }
    return { lat: closestPoint.lat(), lng: closestPoint.lng() };
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds <= 0) return "0";
    if (seconds < 60) return `${seconds} sec`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} min`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hr ${minutes > 0 ? `${minutes} min` : ""}`;
  };

  // Build deviceId → vehicle name map from stopData routes
  const vehicleNameMap = useMemo(() => {
    const map = {};
    if (!stopData) return map;
    stopData.routes.forEach((route) => {
      (route.vehicles || []).forEach((v) => {
        map[v.device_id] = v.name || v.vehicle_name || v.device_id;
      });
    });
    return map;
  }, [stopData]);

  // Fetch vehicle directions from Redis for SSE vehicles
  useEffect(() => {
    if (!vehicleIds.length) return;
    const fetchDirections = async () => {
      const dirs = {};
      await Promise.all(
        vehicleIds.map(async (deviceId) => {
          const dir = await getVehicleDirection(deviceId);
          if (dir) dirs[deviceId] = dir;
        }),
      );
      setVehicleDirections((prev) => ({ ...prev, ...dirs }));
    };
    fetchDirections();
    const intervalId = setInterval(fetchDirections, 30000);
    return () => clearInterval(intervalId);
  }, [vehicleIds]);

  // Fetch user permissions
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userState"));
    if (user) {
      setUserState(user);
      const mapPermission = user.features.some(
        (feature) =>
          feature.api_path === "/api/stop/organization" &&
          feature.action === "GET",
      );
      setHasMapPermission(mapPermission);
    }
  }, []);

  // Set stop data on initial load, then enrich with full vehicle data from API
  useEffect(() => {
    const locationStopData = location.state?.selectedStopData;
    const allStop = location.state?.localStopsData;
    if (!locationStopData) {
      setError("No stop data available");
      return;
    }

    // Reset all state
    setAllStopData(allStop);
    setDirections([]);
    setIsInitialized(false);
    setVehicleData({});
    setVehicleHistory({});
    setStoppedVehicles({});
    vehicleHistoryRef.current = {};
    stoppedVehiclesRef.current = {};
    routePathsRef.current = {};
    setIsInitialVehicleLoadComplete(false);
    setVehicleIds([]);

    // Set initial stopData immediately so the loading screen renders
    setStopData(locationStopData);
    stopDataRef.current = locationStopData;

    // Enrich routes with full vehicle arrays from the API, then set vehicleIds
    getAllRoutes()
      .then((allRoutes) => {
        const routesToProcess = locationStopData.assignedRoutes || locationStopData.routes || [];
        const stopRouteIds = new Set(
          routesToProcess.map((r) => r.id).filter(Boolean),
        );

        const enrichedRoutes = routesToProcess.map((stopRoute) => {
          const apiRoute = allRoutes.find((r) => r.id === stopRoute.id);
          return {
            ...stopRoute,
            // Prefer full vehicle list from API; fall back to whatever came with nav-state
            vehicles: apiRoute?.vehicles || stopRoute.vehicles || [],
          };
        });

        const enrichedStopData = { ...locationStopData, routes: enrichedRoutes };
        setStopData(enrichedStopData);
        stopDataRef.current = enrichedStopData;

        const deviceIds = [
          ...new Set(
            enrichedRoutes
              .flatMap((r) => r.vehicles)
              .map((v) => v.device_id)
              .filter(Boolean),
          ),
        ];
        console.log(
          `[StopMonitor] ${deviceIds.length} vehicle(s) found for this stop:`,
          deviceIds,
        );
        setVehicleIds(deviceIds);
      })
      .catch((err) => {
        console.error("[StopMonitor] Failed to enrich vehicle data:", err);
        // Fallback: use whatever vehicles came with the navigation state
        const routesToProcess = locationStopData.assignedRoutes || locationStopData.routes || [];
        const deviceIds = routesToProcess
          .flatMap((r) => r.vehicles)
          .map((v) => v.device_id)
          .filter(Boolean);
        setVehicleIds([...new Set(deviceIds)]);
      });
  }, [location.state]);

  useEffect(() => {
    stopDataRef.current = stopData;
  }, [stopData]);

  // Manage SSE connection
  useEffect(() => {
    if (
      !stopData ||
      !isInitialized ||
      !isInitialVehicleLoadComplete ||
      vehicleIds.length === 0
    ) {
      console.log("No vehicle IDs, skipping SSE connection");
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        isConnectedRef.current = false;
      }
      return;
    }

    const connectSSE = () => {
      if (isConnectedRef.current) {
        console.log("Already connected, skipping new connection");
        return;
      }

      const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      console.log(`Attempting to connect to SSE URL: ${sseUrl}`);

      if (eventSourceRef.current) {
        console.log("Closing previous SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const source = new EventSource(sseUrl);
      eventSourceRef.current = source;

      source.onopen = () => {
        console.log("SSE connection opened successfully");
        isConnectedRef.current = true;
      };

      source.onerror = (error) => {
        console.error("SSE connection error:", error);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        isConnectedRef.current = false;
        if (!reconnectTimeoutRef.current) {
          console.log("Scheduling reconnection in 5 seconds");
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Reconnecting SSE...");
            connectSSE();
          }, 5000);
        }
      };

      source.onmessage = (event) => {
        try {
          console.log("Received SSE message:", event.data);
          const data = JSON.parse(event.data);
          handleSSEMessageRef.current?.(data);
        } catch (e) {
          console.error("Error parsing SSE message:", e);
        }
      };
    };

    connectSSE();

    return () => {
      console.log("Component unmounting, cleaning up SSE");
      if (eventSourceRef.current) {
        console.log("Closing SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        console.log("Clearing reconnect timeout");
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      isConnectedRef.current = false;
    };
  }, [vehicleIds, isInitialized, isInitialVehicleLoadComplete, stopData]); // Reconnect only after initial seed data loads

  // Fetch directions — guard with !isInitialized so enriched-stopData update doesn't re-trigger
  useEffect(() => {
    if (stopData && isLoaded && !isInitialized) {
      fetchDirections(stopData.routes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopData, isLoaded, isInitialized]);

  const fetchDirections = async (routes) => {
    if (!routes?.length) {
      setDirections([]);
      routePathsRef.current = {};
      setIsInitialized(true);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    const directionsPromises = routes.map(
      (route) =>
        new Promise((resolve, reject) => {
          const routeCoordinates = [...(route.routeCoordinates || [])].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0),
          );

          if (routeCoordinates.length < 2) {
            reject(new Error(`Route ${route.id} has fewer than 2 coordinates`));
            return;
          }

          const waypoints = routeCoordinates
            .slice(1, -1)
            .map((coord) => ({
              location: new window.google.maps.LatLng(
                parseFloat(coord.latitude),
                parseFloat(coord.longitude),
              ),
              stopover: true,
            }));
          directionsService.route(
            {
              origin: new window.google.maps.LatLng(
                parseFloat(routeCoordinates[0].latitude),
                parseFloat(routeCoordinates[0].longitude),
              ),
              destination: new window.google.maps.LatLng(
                parseFloat(
                  routeCoordinates[routeCoordinates.length - 1].latitude,
                ),
                parseFloat(
                  routeCoordinates[routeCoordinates.length - 1].longitude,
                ),
              ),
              waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                routePathsRef.current[route.id] =
                  result.routes[0].overview_path;
                resolve(result);
              } else {
                reject(new Error(`Error fetching directions for route ${route.id}: ${status}`));
              }
            },
          );
        }),
    );

    try {
      const settledResults = await Promise.allSettled(directionsPromises);
      const successfulDirections = settledResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const failedDirections = settledResults.filter(
        (result) => result.status === "rejected",
      );

      if (failedDirections.length > 0) {
        console.warn(
          "[StopMonitor] Some route directions could not be loaded:",
          failedDirections.map((result) => result.reason?.message || result.reason),
        );
      }

      setDirections(successfulDirections);
      setIsInitialized(true);
    } catch (error) {
      console.error("Error fetching directions:", error);
      setDirections([]);
      setIsInitialized(true);
    }
  };

  const fetchDistanceTimeForVehicle = useCallback(
    async (route, deviceId, activeStopData) => {
      let disTimeData = { distance: 0, eta: 0 };

      try {
        const isExtreme =
          route.routeCoordinates[0].stopId === activeStopData.stopId ||
          route.routeCoordinates[route.routeCoordinates.length - 1].stopId ===
            activeStopData.stopId;

        const disTimeRes = await fetch(`${config.baseURL}/distance/time/stop`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vehicleId: deviceId,
            longitude: activeStopData.longitude,
            latitude: activeStopData.latitude,
            isExtremePoint: isExtreme,
          }),
        });

        disTimeData = await disTimeRes.json();
      } catch (error) {
        console.error(`Error fetching distance/ETA for ${deviceId}:`, error);
      }

      return disTimeData;
    },
    [],
  );

  const loadInitialVehicleLocations = useCallback(async () => {
    if (!stopData || !isInitialized) {
      return;
    }

    if (vehicleIds.length === 0) {
      setIsInitialVehicleLoadComplete(true);
      return;
    }

    try {
      const initialVehicles = await Promise.all(
        vehicleIds.map(async (deviceId) => {
          const route = stopData.routes.find((r) =>
            r.vehicles.some((v) => v.device_id === deviceId),
          );

          if (!route) {
            return null;
          }

          try {
            const response = await fetch(
              `${config.baseURL}/location/latest/${deviceId}`,
            );
            const data = await response.json();

            if (data?.latitude == null || data?.longitude == null) {
              return null;
            }

            const normalizedData = normalizeDeviceData(data);
            const position = {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
            };

            const previousHistory = vehicleHistoryRef.current[deviceId];
            const currentTime = Date.now();
            const timeDiff = currentTime - (previousHistory?.timestamp || 0);
            let isStopped = false;

            if (previousHistory?.position) {
              const distance =
                window.google.maps.geometry.spherical.computeDistanceBetween(
                  new window.google.maps.LatLng(
                    previousHistory.position.lat,
                    previousHistory.position.lng,
                  ),
                  new window.google.maps.LatLng(position.lat, position.lng),
                );

              isStopped = distance < 1 && timeDiff > 30000;
            }

            const disTimeData = await fetchDistanceTimeForVehicle(
              route,
              deviceId,
              stopData,
            );

            return {
              deviceId,
              historyEntry: {
                position,
                timestamp: currentTime,
              },
              isStopped,
              vehicle: {
                position,
                routeId: route.routeId,
                speed: parseFloat(
                  normalizedData.speed ?? normalizedData.Speed ?? 0,
                ),
                distance: disTimeData.distance,
                eta: disTimeData.eta,
                heading: calculateHeading(
                  previousHistory?.position,
                  position,
                ),
              },
            };
          } catch (error) {
            console.error(
              `Error fetching initial location for vehicle ${deviceId}:`,
              error,
            );
            return null;
          }
        }),
      );

      const validInitialVehicles = initialVehicles.filter(Boolean);
      const initialVehicleData = {};
      const latestHistory = { ...vehicleHistoryRef.current };
      const latestStoppedVehicles = { ...stoppedVehiclesRef.current };

      validInitialVehicles.forEach(({ deviceId, vehicle, historyEntry, isStopped }) => {
        initialVehicleData[deviceId] = vehicle;
        latestHistory[deviceId] = historyEntry;
        latestStoppedVehicles[deviceId] = isStopped;
      });

      vehicleHistoryRef.current = latestHistory;
      stoppedVehiclesRef.current = latestStoppedVehicles;
      setVehicleData(initialVehicleData);
      setVehicleHistory(latestHistory);
      setStoppedVehicles(latestStoppedVehicles);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    } finally {
      setIsInitialVehicleLoadComplete(true);
    }
  }, [
    calculateHeading,
    fetchDistanceTimeForVehicle,
    isInitialized,
    normalizeDeviceData,
    snapToRoute,
    stopData,
    vehicleIds,
  ]);

  useEffect(() => {
    if (!stopData || !isInitialized) {
      return;
    }

    loadInitialVehicleLocations();
  }, [stopData, isInitialized, loadInitialVehicleLocations]);

  // Handle SSE messages
  const handleSSEMessage = useCallback(
    async (data) => {
      const activeStopData = stopDataRef.current;

      if (data.type && data.type === "connected") {
        console.log("SSE connected:", data);
        return;
      }

      if (
        activeStopData &&
        data.device_id &&
        data.latitude != null &&
        data.longitude != null
      ) {
        const normalizedData = normalizeDeviceData(data);
        const deviceId = normalizedData.deviceId;
        const position = {
          lat: parseFloat(normalizedData.latitude),
          lng: parseFloat(normalizedData.longitude),
        };

        const route = activeStopData.routes.find((r) =>
          r.vehicles.some((v) => v.device_id === deviceId),
        );
        if (!route) {
          console.warn(`No route found for vehicle ${deviceId}`);
          return;
        }

        const newHistory = { ...vehicleHistoryRef.current };
        const prevPosition = vehicleHistoryRef.current[deviceId]?.position;
        const currentTime = Date.now();
        const lastUpdateTime =
          vehicleHistoryRef.current[deviceId]?.timestamp || 0;
        const timeDiff = currentTime - lastUpdateTime;

        const newStoppedVehicles = { ...stoppedVehiclesRef.current };
        if (prevPosition) {
          const distance =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              new window.google.maps.LatLng(prevPosition.lat, prevPosition.lng),
              new window.google.maps.LatLng(position.lat, position.lng),
            );
          newStoppedVehicles[deviceId] = distance < 1 && timeDiff > 30000;
        } else {
          newStoppedVehicles[deviceId] = false;
        }

        newHistory[deviceId] = { position, timestamp: currentTime };
        vehicleHistoryRef.current = newHistory;
        stoppedVehiclesRef.current = newStoppedVehicles;

        const disTimeData = await fetchDistanceTimeForVehicle(
          route,
          deviceId,
          activeStopData,
        );

        setVehicleData((prev) => ({
          ...prev,
          [deviceId]: {
            position,
            routeId: route.routeId,
            speed: parseFloat(normalizedData.speed ?? normalizedData.Speed ?? 0),
            distance: disTimeData.distance,
            eta: disTimeData.eta,
            heading: calculateHeading(prevPosition, position),
          },
        }));

        setVehicleHistory(newHistory);
        setStoppedVehicles(newStoppedVehicles);
      } else {
        console.warn("Invalid SSE data:", data);
      }
    },
    [calculateHeading, fetchDistanceTimeForVehicle, normalizeDeviceData, snapToRoute],
  );

  useEffect(() => {
    handleSSEMessageRef.current = handleSSEMessage;
  }, [handleSSEMessage]);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);
  // Fetch initial data
  const fetchInitialData = async () => {
    try {
      const [vehicles, routes, stops] = await Promise.all([
        getAllVehicles(),
        getAllRoutes(),
        getAllStops(),
      ]);

      // Initialize filtered data with all data

      setFilteredVehicles(vehicles);
      setFilteredRoutes(routes);
      setFilteredStops(stops);
    } catch (error) {
      console.log("Error fetching initial data:", error);
    }
  };
  useEffect(() => {
    fetchInitialData();
  }, []);

  // ─── Stop label collision offsets ─────────────────────────────────────────────
  const stopLabelOffsets = useMemo(() => {
    if (!map || !allStopData?.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = allStopData.map((s) => ({
      id: s.id,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
      name: s.name,
    }));
    return computeStopLabelOffsets(projection, map.getZoom(), entries, {
      BASE_OFFSET: 30,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, allStopData, zoom]);

  // ─── Vehicle label collision offsets ──────────────────────────────────────────
  const vehicleLabelOffsets = useMemo(() => {
    if (!map || !Object.keys(vehicleData).length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = Object.entries(vehicleData).map(([deviceId, data]) => {
      const direction = vehicleDirections[deviceId];
      const hasArrow = !!(direction && direction !== "unknown");
      return {
        deviceId,
        lat: data.position.lat,
        lng: data.position.lng,
        name: vehicleNameMap[deviceId] || "",
        hasArrow,
      };
    });
    return computeVehicleLabelOffsets(projection, map.getZoom(), entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vehicleData, zoom, vehicleDirections]);

  const mapStyles = [
    {
      featureType: "all",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ visibility: "on" }, { color: "black" }],
    },
  ];

  const mapContainerStyle = {
    width: "100%",
    height: "550px",
    border: "1px solid #000000",
    marginTop: "20px",
    borderRadius: "20px",
  };

  const renderMap = () => {
    const center = {
      lat: parseFloat(stopData.latitude),
      lng: parseFloat(stopData.longitude),
    };
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(center);
    allStopData?.forEach((stop) => {
      bounds.extend({
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
      });
    });
    return (
      <Box
        sx={{
          borderRadius: "10px",
          padding: { xs: "10px", sm: "15px", md: "20px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "10px 15px", sm: "10px 20px", md: "20px 40px" }, // Responsive margins
          boxShadow: "-2px 1px 5px 1px #00000040",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            minHeight: "48px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "center", sm: "left" },
            alignItems: { xs: "flex-start", sm: "flex-start" },
            gap: { xs: 1, sm: 0 },
            padding: { xs: "8px 12px", sm: "10px 14px", md: "12px 16px" },
            background: "#E8EAEE",
            boxShadow: "0px 2px 5px 0px #00000040",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "100%" },
            }}
          >
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {t("Longitude")}:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stopData?.longitude || "---"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "100%" },
            }}
          >
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {t("Latitude")}:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stopData.latitude || "---"}
            </Typography>
          </Box>
        </Box>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          onLoad={(mapInstance) => {
            setMap(mapInstance);
            mapInstance.fitBounds(bounds, {
              padding: { top: 50, right: 50, bottom: 50, left: 50 },
            });
            setTimeout(() => mapInstance.setCenter(center), 100);
          }}
          onUnmount={onUnmount}
          onZoomChanged={() => {
            if (map) setZoom(map.getZoom());
          }}
          options={{
            styles: mapStyles,
            zoom: 16.5,
            minZoom: 3,
            maxZoom: 20,
            zoomControl: true,
          }}
        >
          {/* ── Stop markers with MarkerClusterer and collision-safe labels ── */}
          <MarkerClusterer
            options={(() => {
              const stopIcon = getIntermediateStopIcon();
              const clusterStyle = stopIcon
                ? {
                    url: stopIcon.url,
                    width: 40,
                    height: 40,
                    textColor: "#000000",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [40, 20],
                    anchorText: [-2, 0],
                  }
                : {
                    url:
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(
                        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                          <circle cx="20" cy="20" r="18" fill="#d32f2f" stroke="#fff" stroke-width="2"/>
                        </svg>`,
                      ),
                    width: 40,
                    height: 40,
                    textColor: "#ffffff",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [20, 20],
                    anchorText: [0, 0],
                  };
              return {
                styles: [clusterStyle, clusterStyle, clusterStyle],
                gridSize: 20,
                minimumClusterSize: 2,
                zoomOnClick: true,
              };
            })()}
          >
            {(clusterer) =>
              (allStopData || []).map((stop) => {
                const isSelected = stop.stopId === stopData.stopId;
                const offsetPx = stopLabelOffsets[stop.id]?.topPx ?? 52;
                const connectorH = offsetPx - 26;
                return (
                  <React.Fragment key={stop.id}>
                    {(zoom === null || zoom >= 14) && (
                      <OverlayView
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div style={{ position: "relative", left: "0px", top: "0px" }}>
                          {/* Name label */}
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              bottom: `${offsetPx}px`,
                              backgroundColor: "white",
                              color: isSelected ? "blue" : "black",
                              fontWeight: isSelected ? 600 : 500,
                              padding: "3px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                              whiteSpace: "nowrap",
                              zIndex: 1001,
                              pointerEvents: "none",
                            }}
                          >
                            {stop.name}
                          </div>
                          {/* Connector line */}
                          {connectorH > 0 && (
                            <svg
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                bottom: "26px",
                                pointerEvents: "none",
                                overflow: "visible",
                                zIndex: 1000,
                              }}
                              width="2"
                              height={connectorH}
                            >
                              <line
                                x1="1" y1="0" x2="1" y2={connectorH}
                                stroke="rgba(0,0,0,0.45)"
                                strokeWidth="1.5"
                                strokeDasharray="3 2"
                              />
                            </svg>
                          )}
                        </div>
                      </OverlayView>
                    )}
                    <Marker
                      position={{
                        lat: parseFloat(stop.latitude),
                        lng: parseFloat(stop.longitude),
                      }}
                      title={stop.name}
                      icon={
                        isSelected
                          ? {
                              url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                              scaledSize: new window.google.maps.Size(40, 40),
                            }
                          : getIntermediateStopIcon() || undefined
                      }
                      clusterer={isSelected ? undefined : clusterer}
                    />
                  </React.Fragment>
                );
              })
            }
          </MarkerClusterer>

          {/* ── Route polylines hidden; showing proximity-based vehicles only ── */}

          {/* ── 100m monitoring circle around the selected stop ── */}
          <Circle
            center={{
              lat: parseFloat(stopData.latitude),
              lng: parseFloat(stopData.longitude),
            }}
            radius={100}
            options={{
              strokeColor: "#20A8E0",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: "#20A8E0",
              fillOpacity: 0.12,
              zIndex: 1,
            }}
          />

          {/* ── Vehicle markers: only those within 200m of the stop ── */}
          {Object.entries(vehicleData)
            .filter(([, data]) => {
              if (!data?.position) return false;
              const stopLatLng = new window.google.maps.LatLng(
                parseFloat(stopData.latitude),
                parseFloat(stopData.longitude),
              );
              const vehicleLatLng = new window.google.maps.LatLng(
                data.position.lat,
                data.position.lng,
              );
              const dist =
                window.google.maps.geometry.spherical.computeDistanceBetween(
                  stopLatLng,
                  vehicleLatLng,
                );
              return dist <= 1000; // show vehicles within 1 km of the stop
            })
            .map(([deviceId, data]) => {
              const direction = vehicleDirections[deviceId];
              const vehicleIcon = getCustomVehicleIcon(
                deviceId,
                data.heading || 0,
                direction,
                zoom,
              );
              const vehicleName = vehicleNameMap[deviceId] || deviceId;
              const status = vehicleStatuses[deviceId];
              const topPx = vehicleLabelOffsets[deviceId]?.topPx ?? 10;
              const connectorH = topPx;
              const isHovered = hoveredVehicle === deviceId;

              // Build tooltip info
              const vehicleRoute = stopData.routes.find((r) =>
                r.vehicles.some((v) => v.device_id === deviceId),
              );
              const vehicleObj = vehicleRoute?.vehicles?.find(
                (v) => v.device_id === deviceId,
              );
              const driverName =
                vehicleObj?.driver_name ||
                vehicleObj?.driverName ||
                vehicleObj?.driver ||
                "N/A";
              const routeName = vehicleRoute?.name || vehicleRoute?.routeId || "N/A";

              return (
                <React.Fragment key={deviceId}>
                  <OverlayView
                    position={data.position}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: "13px",
                        left: "0px",
                        width: "2px",
                        height: "2px",
                      }}
                    >
                      {(zoom === null || zoom >= 14) && (
                        <>
                          {/* Connector */}
                          {connectorH > 4 && (
                            <svg
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                top: "0px",
                                pointerEvents: "none",
                                overflow: "visible",
                                zIndex: 999,
                              }}
                              width="2"
                              height={connectorH}
                            >
                              <line
                                x1="1" y1="0" x2="1" y2={connectorH}
                                stroke="black"
                                strokeWidth="1.8"
                                strokeDasharray="3 2"
                              />
                            </svg>
                          )}
                          {/* Vehicle name label */}
                          <div
                            style={{
                              position: "absolute",
                              top: vehicleLabelOffsets[deviceId]?.top ?? "10px",
                              left: vehicleLabelOffsets[deviceId]?.left ?? "50%",
                              transform:
                                vehicleLabelOffsets[deviceId]?.transform ??
                                "translateX(-50%)",
                              color: status === "INACTIVE" ? "#000" : "#fff",
                              fontSize: "8px",
                              fontWeight: 600,
                              fontFamily: "Arial, sans-serif",
                              lineHeight: "12px",
                              padding: "6px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              backgroundColor:
                                status === "DEAD" || status === "INACTIVE"
                                  ? "#F9E719"
                                  : "#008C23",
                              borderRadius: "10px",
                              zIndex: 1000,
                              border: "1.5px solid #008C23",
                            }}
                          >
                            {vehicleName}
                          </div>

                        </>
                      )}
                    </div>
                  </OverlayView>
                  <Marker
                    position={data.position}
                    title={vehicleName}
                    icon={vehicleIcon || undefined}
                  />
                </React.Fragment>
              );
            })}
        </GoogleMap>
      </Box>
    );
  };

  const renderTable = () => {
    let sortedVehicles = Object.entries(vehicleData).sort((a, b) => {
      const distA = parseFloat(a[1].distance);
      const distB = parseFloat(b[1].distance);
      if (isNaN(distA)) return 1;
      if (isNaN(distB)) return -1;
      return distA - distB;
    });
    return (
      <>
        <Box
          sx={{
            overflowX: "auto",
            borderRadius: "10px",
            padding: "20px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #A5DCF3",
            margin: { xs: "10px 15px", sm: "10px 20px", md: "10px 40px" }, // Responsive margins
            boxShadow: "-2px 1px 5px 1px #00000040",
          }}
        >
          <Table
            style={{
              width: "100%",
              marginTop: "20px",
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow
                style={{ backgroundColor: "#647490", borderRadius: "8px" }}
              >
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    paddingLeft: "24px",
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  }}
                >
                  {t("Vehicle")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {t("Route")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {t("Distance")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {t("ETA")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    textAlign: "right",
                    padding: "8px",
                    paddingRight: "24px",
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px",
                  }}
                >
                  {t("Speed")}(km/h)
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVehicles.length > 0 ? (
                sortedVehicles.map(([deviceId, data]) => (
                  <TableRow key={deviceId}>
                    <TableCell
                      style={{
                        paddingLeft: "24px",
                        fontWeight: "400",
                        fontSize: "16px",
                      }}
                    >
                      {vehicleNameMap[deviceId] || deviceId || "---"}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {data.routeId || "---"}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {parseFloat(data.distance).toFixed(1) || "---"} km
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {formatTime(data.eta) || "---"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {data.speed || "---"} km/h
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: "center" }}>
                    {t("No vehicles currently available")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </>
    );
  };

  const renderRouteTable = () => (
    <>
      <div
        style={{
          paddingTop: "32px",
          display: "flex",
          marginBottom: "32px",
          flexDirection: isLargeScreen ? "row" : "column",
        }}
      >
        <div style={{ flex: 3 }}>{renderStopDetails()}</div>
        <div style={{ flex: 7, marginRight: "20px" }}>
          <div style={{ flex: 1, marginRight: "20px" }}>
            <div
              className="form_header form_title"
              style={{
                marginBottom: "0px",
                height: "80px",
                borderRadius: "8px",
                backgroundColor: "#647490",
                fontSize: "28px",
              }}
            >
              {t("Assigned Routes")}
            </div>
            <div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "-20px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f2f2f2" }}>
                    <th
                      style={{
                        padding: "24px",
                        textAlign: "left",
                        background: "rgba(32, 168, 224, 1)",
                        color: "white",
                        fontWeight: "600",
                      }}
                    >
                      {t("Route ID")}
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        background: "rgba(32, 168, 224, 1)",
                        textAlign: "right",
                        color: "white",
                        paddingRight: "24px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Route Name")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stopData.routes.map((route) => (
                    <tr key={route.routeId}>
                      <td style={{ padding: "8px", paddingLeft: "24px" }}>
                        {route.routeId}
                      </td>
                      <td style={{ paddingRight: "20px", textAlign: "right" }}>
                        {route.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              itemsPerPage={10}
              currentPage={1}
              onPageChange={(page) => console.log(page)}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderStopDetails = () => (
    <div style={{ margin: "0px 24px" }}>
      <div
        className="form_header form_title"
        style={{
          marginBottom: "0px",
          height: "50px",
          borderRadius: "8px",
          backgroundColor: "#647490",
          fontSize: "28px",
        }}
      >
        {t("Stop Details")}
      </div>
      <div style={{ marginTop: "32px", lineHeight: "40px" }}>
        <span style={{ fontWeight: "600" }}>{t("Stop ID")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.stopId}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Total Routes")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.routes.length}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Stop Name")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.name}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Longitude")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.longitude}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Latitude")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.latitude}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: "2px",
          flexDirection: isLargeScreen ? "row" : "column",
        }}
      >
        <div
          className="customButton_add"
          style={{
            background: "#F9E719",
            color: "black",
            fontSize: "19px",
            whiteSpace: isLargeScreen ? "nowrap" : "normal",
          }}
        >
          {t("View Stop Schedules")}
        </div>
        <div
          className="customButton_add"
          onClick={() =>
            navigate(`/track/screen/${selectedStopData.id}`, {
              state: { selectedStopData, localStopsData },
            })
          }
          style={{
            marginLeft: isLargeScreen ? "10px" : "0px",
            background: "rgba(32, 168, 224, 1)",
            color: "white",
            whiteSpace: isLargeScreen ? "nowrap" : "normal",
            cursor: "pointer",
          }}
        >
          {t("Stop View")}
        </div>
      </div>
    </div>
  );

  if (loadError) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div>Map cannot be loaded right now, sorry.</div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            textAlign: "center",
          }}
        >
          <p>{error}</p>
        </div>
      </>
    );
  }

  if (!isLoaded || !stopData || !isInitialized || !isInitialVehicleLoadComplete) {
    return (
      <LoadingScreen
        t={t}
        navigate={navigate}
        selectedStopData={selectedStopData}
        localStopsData={localStopsData}
      />
    );
  }

  const handleViewStop = () => {
    navigate(
      `/track/screen/${selectedStopData.id}?org=${selectedStopData.organizationId}&InfoSide=right`,
      {
        state: { selectedStopData, localStopsData },
      },
    );
  };

  const handleStopSchedules = () => {
    navigate("/stop/Schedule/Details", {
      state: { id: selectedStopData.id },
    });
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title={t("Monitor Stop")}
        path={t(
          `${orgName || "organization"} > Stops > ${
            stopData?.name || "Monitor Stop"
          } > Monitor`,
        )}
        track={"Stop View"}
        add={"View Schedules"}
        onTrackClick={handleViewStop}
        onAddClick={handleStopSchedules}
      />

      <Box
        sx={{
          borderRadius: "10px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
          justifyContent: "space-between",
          padding: { xs: "15px", sm: "15px 30px", md: "20px 60px" }, // Responsive padding
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "0px 15px", sm: "0px 20px", md: "0px 40px" }, // Responsive margins
          boxShadow: "-2px 1px 5px 1px #00000040",
          gap: { xs: 2, sm: 0 }, // Add gap between items on small screens
        }}
      >
        {/* Stop Name Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 }, // Bottom margin only on small screens
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Stop Name")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "18px", md: "24px" }, // Responsive font size
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {stopData.name}
          </Typography>
        </Box>

        {/* Divider - horizontal on small screens, vertical on larger */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />

        {/* Stop ID Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 }, // Bottom margin only on small screens
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Stop ID")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "18px", md: "24px" }, // Responsive font size
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
            }}
          >
            {stopData.stopId}
          </Typography>
        </Box>

        {/* Second divider */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />

        {/* Connected Routes Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 }, // Bottom margin only on small screens
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Connected Routes")}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "18px", md: "24px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
              }}
            >
              {stopData.routes.length}
            </Typography>
          </Box>
        </Box>

        {/* Third divider */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />

        {/* Vehicles Stop Here Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Vehicles Stop Here")}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: { xs: "4px 12px", md: "4px 16px" },
              borderRadius: "4px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "18px", md: "24px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
              }}
            >
              {0 || "---"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* {renderRouteTable()} */}
      {hasMapPermission && renderMap()}
      {renderTable()}
      <Footer />
    </>
  );
};

export default AdminStopView;
